import { action, observable } from 'mobx';
import stores from 'cd-eft/models/Stores';
import { EngineeringMapService } from 'cd-eft/framework/mapServices/EngineeringMapServices';
import MapView from '@arcgis/core/views/MapView';
import { appConfig } from 'cd-eft/config/AppConfig';
import { UgoDesignToolsController } from 'cd-eft/services/eftSetApi';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';

export interface IUgoSummaryStation {
  select?: boolean;
  ugoName?: string;
  alignmentName?: string;
  alignedElementDesignParamsId?: number,
  alignmentDesignElementJsonId?: number,
  numOfDrains?: number;
  connected?: boolean;
  warnings?: string | null;
  createdDate?: number;
  lastChangeDate?: number;
  errorResponse?: any
}

interface IAllNotifications {
  surface: number,
  alignment: number,
  hydrology: number,
  designControl: number,
  drains: number,
}

const { designStore, ugoDesignStore: ugoDesignStore } = stores.AppStore;

export class UGOSummaryVM {
  private mapView: MapView | undefined;
  @observable history;

  constructor(history) {
    this.history = history;
    if (EngineeringMapService.instance?.mapView) {
      this.mapView = EngineeringMapService.instance.mapView;
    }
  }

  @observable notificationDictionary: { [key: number]: IAllNotifications | null } = {};
  @observable listUGONotificationResponses: any[] = [];

  @observable headerTitle = "";
  @observable onClickInformation = false;
  @observable showIcon: boolean = false;
  @observable isLoading: boolean = false;

  @action.bound onClickInformationToggle = (title) => {
    this.headerTitle = title
    this.onClickInformation = !this.onClickInformation
  }

  @action.bound proceedToPage(page: 'input' | 'design' | 'template') {
    this.history.push(`/design/${designStore.projectsStore.currentProjectId}/ugo/${ugoDesignStore.ugoDesignObject.alignedElementDesignParamsId}/${page}`);
  }

  @action.bound async simulateRunoff() {
    // Will implement later
  }

  @action.bound async combinedLoad() {
    designStore.designElementTypeId = appConfig.hardCodes.designElementTypeUGO; //To enable PEM ToolBar on the map for UGO
    this.getUgoSummary();
  }

  @action.bound async getUgoSummary() {
    this.isLoading = true;
    try {
      // call API to get summary list
      const response = await UgoDesignToolsController.getUgoSummariesBySessionUuidUsingGET(stores.AppStore.designStore.projectsStore.currentSessionId);
      if (response.data && response.data.ugoSummaryDTOs) {
        ugoDesignStore.ugoSummaryList = response.data.ugoSummaryDTOs.map((ugo) => {
          return {
            ...ugo,
            select: false
          }
        });
      }
    } catch (error) {
      MessageCenter.addError((error as Error).message);
    } finally {
      this.isLoading = false;
    }
  }

  notificationStatusOR(elementId: number): boolean {
    const target = this.notificationDictionary[elementId];
    if (!target) {
      return false;
    }
    return !!target.alignment || !!target.designControl || !!target.hydrology || !!target.drains || !!target.surface;
  }

  @action.bound handleCheckboxChange(index: number) {
    const item = ugoDesignStore.ugoSummaryList[index];
    item.select = !item.select;
  }

  @action.bound newUGOProject() {
    designStore.designElementTypeId = appConfig.hardCodes.designElementTypeUGO; //To enable the PEM Toolbar in the map view when adding a new UGO.
    // Will implement later
    ugoDesignStore.reset();
    this.proceedToPage('input');
  }

  @action.bound async refreshElements() {
    if (this.mapView) {
      ugoDesignStore.ugoLayers.forEach((layer) => {
        this.mapView!.map.remove(layer);
        layer.destroy();
      })
    };

    if (ugoDesignStore.ugoSummaryList.length > 0) {
      const selectedugos = [...ugoDesignStore.ugoSummaryList].reverse().filter(e => e.select);

      //clear terraces
      stores.AppStore.terraceDesignStore.terraceLayers.forEach(layer => {
        if (this.mapView) {
          this.mapView.map.remove(layer);
        }
        layer.destroy();
      });

      if (selectedugos.length === 0) {
        if (this.mapView && !designStore.hideSingleCrossSection) designStore.validateSingleCrossSection(this.mapView);
        return;
      }
      try {

        this.isLoading = true;

        //loads Ugo shapes for the selected UGOs
        for (const ugos of selectedugos) {
          const ugoId = Number(ugos.alignedElementDesignParamsId);
          const ugoTitle = ugos.alignmentName == undefined ? "" : ugos.alignmentName;
          const response = await UgoDesignToolsController.getUgoAlignedElementShapeByAlignedElementIdUsingGET(ugoId)

          //show Terrace data for the selected ugo
          const terraceData = response.data.terracesIntersectingWithUgo == undefined ? [] : response.data.terracesIntersectingWithUgo;
          for (const e of terraceData) {
            const terraceId = e.alignedElementDesignParametersId ?? -1;
            const terraceTitle = e.alignedFeatureName ?? `Terrace ${terraceId}`;

            if (terraceId !== -1 && e.terraceStructureShapeDTO && this.mapView) {
              stores.AppStore.terraceDesignStore.addTerraceToMap(
                e.terraceStructureShapeDTO,
                this.mapView,
                terraceId,
                terraceTitle
              );
            } else {
              console.error(`No shape data for terrace ID ${terraceId}`);
            }
          }

          //show ugo line for the selected 
          if (response && response.data && response.data.ugoAlignmentCoordinates) {
            const ugoAlignmentCoordinates = response.data.ugoAlignmentCoordinates;
            if (!ugoAlignmentCoordinates) {
              throw new Error("Failed to retrieve channel data from the service.");
            }
            if (this.mapView) {
              const layerTitle = (ugoTitle.length > 0 ? ugoTitle : `UGO ${ugoId}`);
              ugoDesignStore.addUGOToMap(
                ugoAlignmentCoordinates,
                this.mapView,
                ugoId,
                layerTitle
              );
            }
          } else {
            console.error(`Failed to retrieve terrace data for ID ${ugoId}`);
          }
        }

      } catch (error) {
        console.error("Error refreshing elements:", error);
        MessageCenter.addError((error as Error).message);
      } finally {
        // designStore.setLoader("");
        this.isLoading = false;
        if (this.mapView && !designStore.hideSingleCrossSection) designStore.validateSingleCrossSection(this.mapView);
      }
    }
  }

  @action.bound async editSummaryStation(e: any, item: any) {
    const page = 'input';
    this.history.push(`/design/${designStore.projectsStore.currentProjectId}/ugo/${item.alignedElementDesignParamsId}/${page}`)
  }

  @action.bound async deleteSummaryStation(e: any, item: any) {
    this.isLoading = true;
    try {
      // call delete UGO API
      const response = await UgoDesignToolsController.deleteUgoByAlignedElementDesignParamsIdUsingPOST(item.alignedElementDesignParamsId)
      if (response && !response.data.errorResponse) {
        // call summary UGO API
        await this.getUgoSummary();
      } else if (response.data.errorResponse) {
        MessageCenter.addNotification(response.data.errorResponse.errorMessage!, response.data.errorResponse.errorType!, response.data.errorResponse.errorCode);
      }
    } catch (error) {
      MessageCenter.addError((error as Error).message);
    } finally {
      this.isLoading = false;
    }
  }

  elementActions = {
    'Edit': { action: this.editSummaryStation, disabled: false },
    'Delete': { action: this.deleteSummaryStation, disabled: false },
  };
}